import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { fmtClock, todayStr } from "../domain/date";
import { SESSION_LABEL, sessionElapsedMs, treadmillPosition } from "../domain/session";
import { pingSession, resumeSessionPing } from "../lib/appBridge";
import { strideMeters } from "../domain/energy";
import { beep, buzz, cancelNotification, scheduleNotification } from "../lib/device";
import { useDocumentVisible } from "../lib/useDocumentVisible";
import { useKaya, usePlayerMode } from "../store/kayaStore";
import { Button } from "./Button";
import { I } from "./icons";

/**
 * The open session, global (SNIPPETS §6): a pulsing dot, "Walk · 12:34", End and Resume.
 *
 * Mounted once, in App.tsx, as a flex item in ion-tabs' bottom slot just before the tab bar — so
 * the outlet shrinks to make room and no page needs to know about it. It reads `session`, which
 * is `LocalMeta.session`, never the synced blob (invariant 16).
 *
 * Resume is a real tap that re-announces the session to the counter app: Chrome drops an
 * `intent:` launch no gesture asked for, so this must never happen on mount. End confirms inline
 * ("End walk?" Yes / No) and then does exactly what SessionBanner's End did. (ADR-027)
 *
 * `hideUnder` hides it on any path starting with one of those prefixes (the players, sign-in);
 * `hideAt` on exactly those paths (Today, whose hero shows the session in full).
 */
export function LiveBar({ hideUnder = [], hideAt = [] }: { hideUnder?: string[]; hideAt?: string[] }) {
  const history = useHistory();
  const { pathname } = useLocation();
  const session = useKaya((s) => s.session);
  const S = useKaya((s) => s.S);
  const endSession = useKaya((s) => s.endSession);
  const player = usePlayerMode();
  const visible = useDocumentVisible();
  const [now, setNow] = useState(() => Date.now());
  const [confirming, setConfirming] = useState(false);

  const shown =
    !!session && !hideAt.includes(pathname) && !hideUnder.some((p) => pathname.startsWith(p));
  // A workout's clock lives on its own screen; from here it is simply open.
  const running = !!session && (session.kind === "workout" || session.since !== null);

  // Tick only while it can be seen and the clock is moving: a paused walk's time does not change.
  useEffect(() => {
    if (!shown || !visible || !running) return;
    setNow(Date.now());
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [shown, visible, running]);

  // A minimised treadmill run still changes speed on the machine. With a window visible the
  // service worker stays quiet (invariant 17), so the edge is marked here, with the player's own
  // sounds — on Today too, where the bar itself is hidden. The player marks its own edges.
  const tmRunning = session?.kind === "treadmill" && session.since !== null;
  const inPlayer = pathname.startsWith("/treadrun");
  const edge = useRef<{ sid: string; idx: number; over: boolean } | null>(null);
  useEffect(() => {
    if (!tmRunning || inPlayer || !visible) {
      edge.current = null;
      return;
    }
    const check = () => {
      const st = useKaya.getState();
      const s = st.session;
      if (!s || s.kind !== "treadmill" || s.since === null) return;
      const pos = treadmillPosition(s, Date.now());
      const prev = edge.current;
      edge.current = { sid: s.sid, idx: pos.idx, over: pos.over };
      // The first look only takes a bearing: an edge passed while nothing was watching is
      // history, not a cue.
      if (!prev || prev.sid !== s.sid) return;
      const { sound, vibrate } = st.S.profile;
      if (pos.over && !prev.over) {
        beep(1318, 400, 0.2, sound);
        buzz([200, 100, 200, 100, 200], vibrate);
      } else if (!pos.over && pos.idx !== prev.idx) {
        const ph = s.phases?.[pos.idx];
        beep(ph?.c === "brisk" ? 1046 : 660, 300, 0.2, sound);
        buzz([200, 100, 200], vibrate);
        // The player queued a cue for one edge only; queue the next, as the player would have,
        // so it still sounds if the screen goes off from here.
        const nx = s.phases?.[pos.idx + 1];
        if (nx) scheduleNotification("tm", Date.now() + pos.leftMs, "Treadmill: " + nx.l, "Set " + nx.s.toFixed(1) + " km/h");
      }
    };
    check();
    const t = window.setInterval(check, 1000);
    return () => window.clearInterval(t);
  }, [tmRunning, inPlayer, visible]);

  // The Toast is fixed to the viewport, so it has to be told the bar is there.
  useEffect(() => {
    document.documentElement.classList.toggle("has-livebar", shown);
    return () => document.documentElement.classList.remove("has-livebar");
  }, [shown]);

  // A different session, or none, starts the End button over.
  const sid = session?.sid;
  useEffect(() => setConfirming(false), [sid, pathname]);

  if (!session || !shown) return null;

  const label = SESSION_LABEL[session.kind];
  // A workout keeps its clock in the day log, where it always has; the record only names it.
  const elapsed =
    session.kind === "workout"
      ? S.logs[todayStr()]?.[session.rescue || session.extra ? "extraElapsed" : "elapsedMs"] || 0
      : sessionElapsedMs(session, now);

  const resume = () => {
    resumeSessionPing(session, strideMeters(S), player);
    // Minimise comes back to the page Resume was tapped on.
    history.push(session.route, { from: pathname });
  };

  const end = () => {
    if (session.kind !== "workout") pingSession("stop", Date.now(), session.sid);
    // The player leaves its next phase cue queued when minimised; an ended run must not ring.
    if (session.kind === "treadmill") cancelNotification("tm");
    endSession();
  };

  return (
    // A group, not a live region: the clock changes every second and a live region would read
    // every one of them out.
    <div slot="bottom" className="k-livebar" role="group" aria-label="Open session">
      <span className={`k-livebar__dot${running ? " is-on" : ""}`} aria-hidden="true" />
      <span className="k-livebar__txt">
        {confirming ? (
          <span className="k-livebar__main">End {label.toLowerCase()}?</span>
        ) : (
          <>
            <span className="k-livebar__main" aria-live="off">
              {label} · {fmtClock(elapsed)}
            </span>
            {running ? null : <span className="k-livebar__sub">Paused</span>}
          </>
        )}
      </span>
      {confirming ? (
        <>
          <Button variant="danger" onClick={end}>
            Yes
          </Button>
          <Button variant="ghost" onClick={() => setConfirming(false)}>
            No
          </Button>
        </>
      ) : (
        <>
          <Button variant="ghost" onClick={() => setConfirming(true)}>
            End
          </Button>
          <Button icon={I.play} onClick={resume}>
            Resume
          </Button>
        </>
      )}
    </div>
  );
}
